import { ASPECT_RATIOS, MAX_IMAGE_SIZE, MAX_MOVIE_SIZE, MAX_PDF_SIZE, MAX_UPLOAD_PDF_EPS_SIZE, MAX_VCARD_SIZE, PRESET_ASPECT_RATIO_MATCH_TOLERANCE, UPLOADABLE_IMAGE_FILE_TYPES, UPLOADABLE_MOVIE_TYPES } from "#lib/components/images/constants.js";
import { MAX_IMAGE_MEGAPIXELS, getImageDimensions, requireImageConversion } from "#lib/image.js";
import { Logger } from "#lib/logging.js";

export async function getContentType(url: string): Promise<string | null> {
	try {
		const searchParams = new URLSearchParams({ image: url });
		const resp = await fetch(`/api/image-proxy?${searchParams}`, { method: "HEAD" });
		if (!resp.ok) {
			const { message } = await resp.json();
			throw new Error(message);
		}
		return resp.headers.get("Content-Type");
	} catch (error) {
		Logger.error("Error fetching content type for image", { err: error });
		return null;
	}
}

export function getMaxFileSize(file: File): number {
	if (UPLOADABLE_IMAGE_FILE_TYPES.includes(file.type)) {
		return MAX_IMAGE_SIZE;
	}
	if (UPLOADABLE_MOVIE_TYPES.includes(file.type)) {
		return MAX_MOVIE_SIZE;
	}
	if (file.type === "application/pdf") {
		return MAX_PDF_SIZE;
	}
	if (file.type === "text/vcard") {
		return MAX_VCARD_SIZE;
	}
	return 0;
}

// Validate an uploaded file and return the size to record on the asset. EPS/PDF
// can't be loaded into an <img>, so they're detected by extension or MIME and
// bounded by the dedicated PDF/EPS limit. Raster images are checked for both
// pixel area and byte size. Throws a user-facing Error when a file is too large.
export async function validateUploadFile(file: File): Promise<{ imageSize: number }> {
	if (requireImageConversion(file.name, file.type)) {
		if (file.size > MAX_UPLOAD_PDF_EPS_SIZE) {
			throw new Error(`PDF/EPS files must be less than ${MAX_UPLOAD_PDF_EPS_SIZE / (1024 * 1024)}MB. Use a smaller file and try again.`);
		}
		return { imageSize: file.size };
	}
	const dimensions = await getImageDimensions(file);
	if (dimensions.width * dimensions.height > MAX_IMAGE_MEGAPIXELS) {
		throw new Error(`The image exceeds the maximum image area of ${MAX_IMAGE_MEGAPIXELS / 1e6} megapixels. Use a smaller image and try again.`);
	}
	const maxSize = getMaxFileSize(file);
	if (maxSize < file.size) {
		throw new Error(`The file exceeds the maximum file size of ${maxSize / (1024 * 1024)}MB. Use a smaller file and try again.`);
	}
	return { imageSize: dimensions.width * dimensions.height };
}

// Ratios reconstructed from pixel geometry (e.g. an existing saved crop) are subject to
// rounding, so snap them onto a known preset when they're close enough to one.
export function matchAspectRatioPreset(ratio: number | undefined): number | undefined {
	if (ratio === undefined || !Number.isFinite(ratio)) {
		return undefined;
	}
	return ASPECT_RATIOS.find(option => option.ratio !== undefined && Math.abs(option.ratio - ratio) < PRESET_ASPECT_RATIO_MATCH_TOLERANCE)?.ratio;
}

/** True when the value is a tokenized data property (e.g. `{{ .data.foo }}`), not a fetchable URL. */
export function isDataProperty(value: string | undefined | null): boolean {
	if (!value) {
		return false;
	}
	const trimmed = value.trim();
	return trimmed.startsWith("{{") && trimmed.endsWith("}}");
}
